import React, { useState, useEffect } from "react";
import { Plus, BarChart3, Filter as FilterIcon } from "lucide-react";
import CreateCampaignForm from "../components/CreateCampaignForm";
import EditCampaignForm from "../components/EditCampaignForm";
import CampaignFilterPanel from "../components/CampaignFilter";
import CampaignAppliedFiltersBar from "../components/campaigns/CampaignAppliedFiltersBar";
import CampaignPagination from "../components/CampaignPagination";
import CampaignCard from "../components/campaigns/CampaignCard";
import ThemeLoader from "../components/ui/ThemeLoader";
import AbortCampaignModal from "../components/AbortCampaignModal";
import RescheduleCampaignModal from "../components/RescheduleCampaignModal";
import UpdateScheduleModal from "../components/UpdateScheduleModal";
import ChangeWhatsappAccountModal from "../components/ChangeWhatsappAccountModal";
import ValidateCampaignModal from "../components/ValidateCampaignModal";
import {
  deleteCampaign,
  resumeCampaign,
  cancelScheduledCampaign,
  refreshCampaignCardStats,
} from "../apis/campaign";
import { requestCampaignLogs } from "../apis/downloadCenter";
import { getTemplateDropdownList } from "../apis/template";
import { toast } from "react-toastify";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import { useNavigate } from "react-router-dom";
import { useCampaignList } from "../hooks/useCampaignList";
import { toModalCampaign } from "../utils/campaignList";
import {
  clearCampaignFilterField,
  emptyCampaignFilters,
  hasActiveCampaignFilters,
} from "../utils/campaignFilterLabels";

const Campaigns = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [filters, setFilters] = useState({
    status: "",
    campaign_name: "",
    template_id: "",
    whatsapp_account_id: "",
    date_from: "",
    date_to: "",
  });

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const {
    campaigns,
    loading,
    isRefreshing,
    pagination,
    reload: reloadCampaigns,
    updateCampaignStats,
  } = useCampaignList({ page: currentPage, perPage, filters });

  const hasPreparingCampaigns = campaigns.some(
    (c) => String(c.status || "").toLowerCase() === "preparing"
  );
  const hasPendingWalletCampaigns = campaigns.some((c) => {
    const pending = Number(c.stats?.pending || 0);
    const status = String(c.status || "").toLowerCase();
    return (
      pending > 0 &&
      ["inactive", "active", "failed", "aborted", "cancelled"].includes(status)
    );
  });

  useEffect(() => {
    if ((!hasPreparingCampaigns && !hasPendingWalletCampaigns) || view !== "list") {
      return undefined;
    }
    const interval = setInterval(() => {
      reloadCampaigns({ silent: true });
    }, hasPreparingCampaigns ? 4000 : 15000);
    return () => clearInterval(interval);
  }, [hasPreparingCampaigns, hasPendingWalletCampaigns, view, reloadCampaigns]);

  const totalItems = pagination.total_items;
  const totalPages = pagination.total_pages;

  const [abortModal, setAbortModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [rescheduleModal, setRescheduleModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [updateScheduleModal, setUpdateScheduleModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [changeAccountModal, setChangeAccountModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [validateModal, setValidateModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const { phoneAccounts, fetchPhoneAccounts, loading: loadingPhoneAccounts } = usePhoneAccount();

  const loadTemplatesForPhone = async (phoneId) => {
    try {
      setLoadingTemplates(true);
      const response = await getTemplateDropdownList({
        limit: 1000,
        offset: 0,
        ...(phoneId ? { phoneId } : {}),
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error("Error loading template dropdown:", err);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchPhoneAccounts("campaigns");
  }, [fetchPhoneAccounts]);

  useEffect(() => {
    if (!showFilters) return;
    loadTemplatesForPhone(filters.whatsapp_account_id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  useEffect(() => {
    if (!hasActiveCampaignFilters(filters)) return;
    if (filters.template_id || filters.whatsapp_account_id) {
      loadTemplatesForPhone(filters.whatsapp_account_id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.template_id, filters.whatsapp_account_id]);

  const handleWhatsappAccountFilterChange = (phoneId) => {
    loadTemplatesForPhone(phoneId || null);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilter = (filtersToApply) => {
    const filtersToUse = filtersToApply || filters;
    setFilters(filtersToUse);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleRemoveAppliedFilter = (field) => {
    const next = clearCampaignFilterField(filters, field);
    setFilters(next);
    setCurrentPage(1);
    if (field === "whatsapp_account_id") {
      loadTemplatesForPhone(null);
    }
  };

  const handleClearAllFilters = () => {
    const cleared = emptyCampaignFilters();
    setFilters(cleared);
    setCurrentPage(1);
    loadTemplatesForPhone(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) {
      return;
    }
    try {
      // API function doesn't need token - it's handled by interceptor
      await deleteCampaign(campaignId);
      toast.success("Campaign deleted successfully");
      if (campaigns.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        reloadCampaigns();
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error(error.response?.data?.error || "Failed to delete campaign");
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      // API function doesn't need token - it's handled by interceptor
      await resumeCampaign(campaignId);
      toast.success("Campaign resuming - processing pending contacts");
      reloadCampaigns();
    } catch (error) {
      console.error("Error resuming campaign:", error);
      toast.error(error.response?.data?.error || "Failed to resume campaign");
      reloadCampaigns();
    }
  };

  const handleDownloadLogs = async (campaignId, campaignName) => {
    try {
      // API function doesn't need token - it's handled by interceptor
      await requestCampaignLogs(campaignId);
      toast.success(
        `Download request created! Check Download Center for logs.`,
        { autoClose: 4000 }
      );
    } catch (error) {
      console.error("Error requesting campaign logs:", error);
      toast.error(
        error.response?.data?.error || "Failed to request campaign logs"
      );
    }
  };

  const handleAbortCampaign = (campaign) => {
    setAbortModal({ isOpen: true, campaign: toModalCampaign(campaign) });
  };

  const handleRescheduleCampaign = (campaign) => {
    const modalCampaign = toModalCampaign(campaign);
    if (!modalCampaign?.campaign_id) {
      console.error("[Campaigns] Invalid campaign for reschedule:", campaign);
      toast.error("Invalid campaign selected");
      return;
    }
    setRescheduleModal({ isOpen: true, campaign: modalCampaign });
  };

  const handleUpdateSchedule = (campaign) => {
    const modalCampaign = toModalCampaign(campaign);
    if (!modalCampaign?.campaign_id) {
      console.error("[Campaigns] Invalid campaign for update schedule:", campaign);
      toast.error("Invalid campaign selected");
      return;
    }
    setUpdateScheduleModal({ isOpen: true, campaign: modalCampaign });
  };

  const handleChangeWhatsappAccount = (campaign) => {
    const modalCampaign = toModalCampaign(campaign);
    if (!modalCampaign?.campaign_id) {
      console.error("[Campaigns] Invalid campaign for change WhatsApp account:", campaign);
      toast.error("Invalid campaign selected");
      return;
    }
    setChangeAccountModal({ isOpen: true, campaign: modalCampaign });
  };

  const handleValidateCampaign = (campaign) => {
    const modalCampaign = toModalCampaign(campaign);
    if (!modalCampaign?.campaign_id) {
      console.error("[Campaigns] Invalid campaign for validate:", campaign);
      toast.error("Invalid campaign selected");
      return;
    }
    setValidateModal({ isOpen: true, campaign: modalCampaign });
  };

  const handleRetargetCampaign = (campaign, contactFilter = 'all') => {
    navigate(`/campaigns/${campaign.campaign_id}/retarget?filter=${contactFilter}`);
  };

  const handleRefreshCardStats = async (campaignId) => {
    try {
      const response = await refreshCampaignCardStats(campaignId);
      if (response.data?.stats) {
        updateCampaignStats(campaignId, response.data.stats, {
          error_message: response.data.error_message ?? null,
        });
      }
    } catch (error) {
      console.error("Error refreshing campaign stats:", error);
      toast.error(error.response?.data?.error || "Failed to refresh numbers");
      throw error;
    }
  };

  const handleViewAnalytics = (campaign) => {
    navigate(`/campaigns/${campaign.campaign_id}/analytics`);
  };

  const handleCancelScheduledCampaign = async (campaignId) => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this scheduled campaign?"
      )
    ) {
      return;
    }
    try {
      // API function doesn't need token - it's handled by interceptor
      const response = await cancelScheduledCampaign(campaignId);
      if (response.data.success) {
        toast.success(
          response.data.message || "Scheduled campaign cancelled successfully"
        );
        reloadCampaigns();
      } else {
        toast.error(
          response.data.message || "Could not cancel scheduled campaign"
        );
      }
    } catch (error) {
      console.error("Error cancelling scheduled campaign:", error);
      toast.error(
        error.response?.data?.error || "Failed to cancel scheduled campaign"
      );
    }
  };

  const handleModalSuccess = () => {
    reloadCampaigns();
  };

  const handleCampaignSubmit = async (campaignData) => {
    try {
      const { createCampaign } = await import("../apis/campaign");
      const response = await createCampaign(campaignData);
      if (response.data?.preparing) {
        toast.success("Campaign created — preparing audience in the background.");
      } else {
        toast.success("Campaign created successfully!");
      }
      setView("list");
      reloadCampaigns();
    } catch (error) {
      console.error("Error creating campaign:", error);
      const errorResponse = error.response?.data;

      if (errorResponse?.validation?.errors) {
        throw error;
      }

      // Don't show error for subscription issues - SubscriptionGate handles it
      if (!errorResponse?.requiresSubscription) {
        const errorMessage =
          errorResponse?.error || error.message || "Failed to create campaign";
        toast.error(errorMessage);
      }
      throw error;
    }
  };

  if (view === "create") {
    return (
      <CreateCampaignForm
        onCancel={() => setView("list")}
        onSubmit={handleCampaignSubmit}
      />
    );
  }

  if (view === "edit" && editingCampaignId) {
    return (
      <EditCampaignForm
        campaignId={editingCampaignId}
        onCancel={() => {
          setView("list");
          setEditingCampaignId(null);
        }}
        onSuccess={() => {
          reloadCampaigns();
        }}
      />
    );
  }

  return (
    <div className="campaigns-page">
      <div>
        <div className="page-head">
          <div>
            <h2>Campaigns</h2>
            <p>Manage your WhatsApp campaigns</p>
          </div>
          <div className="grow" />
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`ping-btn ping-btn-sm ${showFilters || hasActiveCampaignFilters(filters) ? "ping-btn-primary" : "ping-btn-ghost"}`}
          >
            <FilterIcon size={16} />
            Filters
            {hasActiveCampaignFilters(filters) && <em className="camp-filter-badge" />}
          </button>
          <button
            type="button"
            onClick={() => setView("create")}
            className="ping-btn ping-btn-primary ping-btn-sm"
          >
            <Plus size={16} />
            Create campaign
          </button>
        </div>

        <CampaignAppliedFiltersBar
          filters={filters}
          phoneAccounts={phoneAccounts}
          templates={templates}
          onRemoveFilter={handleRemoveAppliedFilter}
          onClearAll={handleClearAllFilters}
          onEditFilters={() => setShowFilters(true)}
          loading={loading || isRefreshing}
        />

        <div className={`camp-grid${isRefreshing ? " is-refreshing" : ""}`}>
          {isRefreshing && campaigns.length > 0 && (
            <div className="camp-empty">
              <ThemeLoader size="sm" message="Updating campaigns…" padded />
            </div>
          )}
          {loading ? (
            <div className="camp-empty">
              <ThemeLoader
                size="lg"
                message="Loading campaigns…"
                submessage="Please wait while your campaigns are fetched"
              />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="camp-empty">
              <div className="camp-empty-ico">
                <BarChart3 size={22} />
              </div>
              <h3>
                {hasActiveCampaignFilters(filters) ? "No campaigns match your filters" : "No campaigns yet"}
              </h3>
              <p>
                {hasActiveCampaignFilters(filters)
                  ? "Try adjusting or clearing your filters to see more results."
                  : "Create your first campaign to get started."}
              </p>
              {hasActiveCampaignFilters(filters) ? (
                <button type="button" onClick={handleClearAllFilters} className="ping-btn ping-btn-primary ping-btn-sm">
                  Clear filters
                </button>
              ) : (
                <button type="button" onClick={() => setView("create")} className="ping-btn ping-btn-primary ping-btn-sm">
                  <Plus size={16} />
                  Create campaign
                </button>
              )}
            </div>
          ) : (
            campaigns.map((campaign) => (
              <CampaignCard
                key={campaign.campaign_id}
                campaign={campaign}
                onRetarget={handleRetargetCampaign}
                onViewAnalytics={handleViewAnalytics}
                onAbort={handleAbortCampaign}
                onResume={handleResumeCampaign}
                onDelete={handleDeleteCampaign}
                onDownload={handleDownloadLogs}
                onUpdateSchedule={handleUpdateSchedule}
                onCancelScheduled={handleCancelScheduledCampaign}
                onReschedule={handleRescheduleCampaign}
                onChangeAccount={handleChangeWhatsappAccount}
                onRefreshStats={handleRefreshCardStats}
              />
            ))
          )}
        </div>

        {!loading && campaigns.length > 0 && (
          <div className="camp-pager-wrap">
            <CampaignPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              perPage={perPage}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        )}
      </div>

      {showFilters && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => setShowFilters(false)} />
          <CampaignFilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilter={handleApplyFilter}
            onClose={() => setShowFilters(false)}
            templates={templates}
            phoneAccounts={phoneAccounts}
            loadingPhoneAccounts={loadingPhoneAccounts}
            loadingTemplates={loadingTemplates}
            onWhatsappAccountChange={handleWhatsappAccountFilterChange}
          />
        </>
      )}

      <AbortCampaignModal
        isOpen={abortModal.isOpen}
        onClose={() => setAbortModal({ isOpen: false, campaign: null })}
        campaign={abortModal.campaign}
        onSuccess={handleModalSuccess}
      />
      <RescheduleCampaignModal
        isOpen={rescheduleModal.isOpen}
        onClose={() => setRescheduleModal({ isOpen: false, campaign: null })}
        campaign={rescheduleModal.campaign}
        onSuccess={handleModalSuccess}
      />
      <UpdateScheduleModal
        isOpen={updateScheduleModal.isOpen}
        onClose={() => setUpdateScheduleModal({ isOpen: false, campaign: null })}
        campaign={updateScheduleModal.campaign}
        onSuccess={handleModalSuccess}
      />
      <ChangeWhatsappAccountModal
        isOpen={changeAccountModal.isOpen}
        onClose={() => setChangeAccountModal({ isOpen: false, campaign: null })}
        campaign={changeAccountModal.campaign}
        phoneAccounts={phoneAccounts}
        onSuccess={handleModalSuccess}
      />
      <ValidateCampaignModal
        isOpen={validateModal.isOpen}
        onClose={() => setValidateModal({ isOpen: false, campaign: null })}
        campaign={validateModal.campaign}
      />
    </div>
  );
};

export default Campaigns;
